/**
 * @file Service API centralisé.
 * @description
 * Ce fichier est le cœur de la communication entre le frontend et le backend.
 * Il configure une instance unique et globale d'Axios qui est ensuite utilisée
 * par tous les autres services spécifiques à une ressource (ex: `lake.service.ts`).
 *
 * Fonctionnalités clés :
 * - **Instance Axios unique:** Exporte une instance `apiService` pré-configurée.
 * - **Intercepteur de Requête:** Ajoute automatiquement le token d'authentification JWT
 *   (récupéré depuis le `authStore`) à l'en-tête `Authorization` de chaque requête sortante.
 * - **Intercepteur de Réponse:**
 *   - Extrait directement le champ `data` des réponses réussies pour simplifier le code
 *     dans les autres services.
 *   - Intercepte les erreurs, les logue de manière détaillée et les enrichit avec un
 *     message `userFriendly` formaté, ce qui permet d'afficher des notifications
 *     d'erreur claires et cohérentes à l'utilisateur.
 * - **Méthodes CRUD:** Fournit des méthodes génériques (`get`, `post`, `put`, etc.)
 *   qui encapsulent les appels Axios.
 * - **Gestion des Fichiers:** Inclut des méthodes spécifiques (`postWithFiles`, etc.) pour
 *   gérer facilement les uploads de `FormData`.
 */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  RawAxiosRequestHeaders,
} from "axios";
import { tGlobal } from "@/i18n";
import apiConfig from "../config/api.config"; // Assurez-vous que ce fichier est aussi en TS ou a des types
import * as apiEndpoints from "../config/api.endpoints"; // Correction du chemin d'importation
import { SecurityService } from "./security.service";
import { classifyError } from "@/utils/errors";
import { sentryService } from "@/services/sentry.service";
// Notification store is imported dynamically to avoid circular dependency
// import { useNotificationStore } from "@/stores/notificationStore";

interface ApiConfig {
  apiKey?: string;
  timeout?: number;
  // Ajoutez d'autres propriétés de apiConfig si nécessaire
}

// Type pour l'erreur formatée
interface UserFriendlyError {
  message: string;
  code: string;
  originalError: unknown;
}

// Étendre AxiosError pour inclure userFriendly
interface CustomAxiosError extends AxiosError {
  userFriendly?: UserFriendlyError;
}

type EndpointFactory = (...args: unknown[]) => string;

/**
 * Service API centralisé pour toutes les opérations CRUD
 * Ce service utilise la configuration globale définie dans api.config.js
 *
 * IMPORTANT: Tous les endpoints doivent provenir de api.endpoints.ts
 * Exemple d'utilisation:
 *
 * import { FISHING_SPOTS } from '@/config/api.endpoints'; // Mettez à jour le chemin si nécessaire
 *
 * // Utilisation correcte avec un endpoint dynamique:
 * const lakeId = '123';
 * apiService.get(FISHING_SPOTS.LIST(lakeId)); // Assurez-vous que FISHING_SPOTS et LIST sont correctement définis
 */

class ApiService {
  public axios: AxiosInstance;
  public readonly endpoints: typeof apiEndpoints; // Ajout de readonly et typage correct

  constructor() {
    const effectiveApiConfig = apiConfig as ApiConfig;

    // Headers sécurisés avec protection complète
    const headers: RawAxiosRequestHeaders = {
      "Content-Type": "application/json",
      "X-API-Key": import.meta.env.VITE_API_KEY,
      "X-Content-Type-Options": "nosniff",
      "X-Frame-Options": "DENY",
      "X-XSS-Protection": "1; mode=block",
      "Strict-Transport-Security":
        "max-age=31536000; includeSubDomains; preload",
      "Referrer-Policy": "strict-origin-when-cross-origin",
      "Permissions-Policy": "geolocation=(), microphone=(), camera=()",
    };

    // Validation stricte des variables d'environnement pour la sécurité
    if (!import.meta.env.VITE_APP_API_URL) {
      throw new Error(
        "La variable d'environnement VITE_APP_API_URL est requise pour la sécurité",
      );
    }

    if (!import.meta.env.VITE_API_KEY) {
      throw new Error(
        "La variable d'environnement VITE_API_KEY est requise pour la sécurité",
      );
    }

    // Validation de l'URL pour éviter les attaques par injection
    try {
      const url = new URL(import.meta.env.VITE_APP_API_URL);
      if (url.protocol !== "https:" && url.protocol !== "http:") {
        throw new Error("L'URL de l'API doit utiliser HTTP ou HTTPS");
      }
    } catch {
      throw new Error(
        `URL d'API invalide: ${import.meta.env.VITE_APP_API_URL}`,
      );
    }

    this.axios = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL || "",
      headers: headers,
      timeout: effectiveApiConfig.timeout || 10000,
      withCredentials: true, // Crucial pour les cookies et la session
      xsrfCookieName: "X-CSRF-TOKEN", // Nom du cookie CSRF envoyé par le backend
      xsrfHeaderName: "X-CSRF-Token", // Nom du header que le backend attend (casse mixte)
    });

    // Assigner les endpoints ici après l'initialisation
    this.endpoints = apiEndpoints;

    // Initialiser le token CSRF depuis les cookies existants
    SecurityService.extractCSRFTokenFromCookies();

    this.axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Les headers CSRF sont maintenant gérés par l'intercepteur de manière plus ciblée
        if (
          ["post", "put", "patch", "delete"].includes(
            config.method?.toLowerCase() || "",
          )
        ) {
          const csrfHeaders = SecurityService.getCSRFHeaders();
          Object.assign(config.headers, csrfHeaders);
          if (!config.headers["X-CSRF-Token"]) {
            const csrfToken = this.getCSRFTokenFromCookie();
            if (csrfToken) {
              config.headers["X-CSRF-Token"] = csrfToken;
            }
          }
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );

    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        // Extraire automatiquement le token CSRF des cookies de réponse
        if (response.headers["set-cookie"]) {
          const cookieHeaders = Array.isArray(response.headers["set-cookie"])
            ? response.headers["set-cookie"]
            : [response.headers["set-cookie"]];

          for (const cookieHeader of cookieHeaders) {
            if (cookieHeader.includes("X-CSRF-TOKEN=")) {
              const match = cookieHeader.match(/X-CSRF-TOKEN=([^;]+)/);
              if (match && match[1]) {
                SecurityService.extractCSRFToken(
                  new Response("", {
                    headers: { "X-CSRF-Token": decodeURIComponent(match[1]) },
                  }),
                );
              }
            }
          }
        }

        return response.data;
      },
      (error: CustomAxiosError) => {
        const formattedError = this.formatErrorForUser(error);
        error.userFriendly = formattedError;

        // Classify and report to Sentry (no-op when disabled)
        const classified = classifyError(error, formattedError.message);
        (error as unknown as Record<string, unknown>)["errorKind"] = classified.kind;
        if (classified.kind === "server" || classified.kind === "unknown") {
          sentryService.captureError(classified, {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
          });
        }

        // Check per-request opt-out flag — callers that handle their own
        // notification can set header 'X-Silent-Error: true' to suppress
        // the auto-notification here.
        const isSilent = error.config?.headers?.["X-Silent-Error"] === "true";

        try {
          if (!isSilent) {
            // Import notification store dynamically to break circular dependency
            import("@/stores/notificationStore").then(({ useNotificationStore }) => {
              const notificationStore = useNotificationStore();
              // Messages spécifiques utiles
              if (
                error.response?.status === 400 &&
                typeof error.response.data === "object"
              ) {
                const detail = (error.response.data as { detail?: string })?.detail || "";
                if (
                  typeof detail === "string" &&
                  detail.toLowerCase().includes("already exists")
                ) {
                  notificationStore.error(
                    tGlobal("auth.register.already_have_account"),
                  );
                } else {
                  notificationStore.error(formattedError.message);
                }
              } else if (error.response?.status === 401) {
                const endpoint = (error.config?.url || "").toString();
                const method = (error.config?.method || "")
                  .toString()
                  .toLowerCase();
                const isInitialMeCheck =
                  method === "get" && endpoint.includes("users/me");
                // Ne pas notifier pour le check initial du profil sur la landing (visiteur anonyme)
                if (!isInitialMeCheck) {
                  notificationStore.warning(tGlobal("errors.unauthorized"));
                  // Session expirée : purger l'identité ET les stores de domaine.
                  // Sinon l'UI restait « authentifiée » (user intact) alors que
                  // chaque requête renvoyait 401, en gardant affichées les
                  // données du tenant. Import dynamique pour éviter le cycle
                  // api.service ↔ authStore.
                  void (async () => {
                    try {
                      const { useAuthStore } = await import("@/stores/authStore");
                      const authStore = useAuthStore();
                      if (authStore.isAuthenticated) {
                        authStore.clearUser();
                        const { resetAllStores } = await import(
                          "@/plugins/piniaReset"
                        );
                        resetAllStores(["auth"]);
                      }
                    } catch {
                      /* purge best-effort : ne jamais masquer l'erreur d'origine */
                    }
                  })();
                }
              } else if (error.response?.status === 403) {
                // formattedError.message already prefers the backend's
                // specific detail (e.g. plan/module gates) over the generic
                // "forbidden" text, same as the 400 branch above.
                notificationStore.warning(formattedError.message);
              } else if (error.response?.status === 404) {
                notificationStore.info(tGlobal("errors.not_found"));
              } else {
                notificationStore.error(formattedError.message);
              }
            });
          }
        // eslint-disable-next-line no-empty
        } catch {}
        throw error;
      },
    );
  }

  /**
   * Définit ou supprime le header d'authentification global pour Axios.
   * @param {string | null} token - Le token JWT à utiliser. Si null, le header est supprimé.
   */
  public setAuthHeader(token: string | null): void {
    if (token) {
      this.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete this.axios.defaults.headers.common["Authorization"];
    }
  }

  public async get<T = unknown>(
    endpoint: string | EndpointFactory,
    params: Record<string, unknown> = {},
  ): Promise<T> {
    const url = typeof endpoint === "function" ? endpoint() : endpoint;
    try {
      return (await this.axios.get<T>(url, { params })) as unknown as T;
    } catch (error) {
      this.logErrorDetails(error as CustomAxiosError);
      throw error;
    }
  }

  public async post<T = unknown>(
    endpoint: string | EndpointFactory,
    data: unknown,
    config: Record<string, unknown> = {},
  ): Promise<T> {
    const url = typeof endpoint === "function" ? endpoint() : endpoint;
    try {
      return (await this.axios.post<T>(url, data, config)) as unknown as T;
    } catch (error) {
      this.logErrorDetails(error as CustomAxiosError);
      throw error;
    }
  }

  public async put<T = unknown>(
    endpoint: string | EndpointFactory,
    data: unknown,
  ): Promise<T> {
    const url = typeof endpoint === "function" ? endpoint() : endpoint;
    try {
      return (await this.axios.put<T>(url, data)) as unknown as T;
    } catch (error) {
      this.logErrorDetails(error as CustomAxiosError);
      throw error;
    }
  }

  public async patch<T = unknown>(
    endpoint: string | EndpointFactory,
    data: unknown = {},
  ): Promise<T> {
    const url = typeof endpoint === "function" ? endpoint() : endpoint;
    try {
      return (await this.axios.patch<T>(url, data)) as unknown as T;
    } catch (error) {
      this.logErrorDetails(error as CustomAxiosError);
      throw error;
    }
  }

  public async delete<T = unknown>(
    endpoint: string | EndpointFactory,
    config: Record<string, unknown> = {},
  ): Promise<T> {
    const url = typeof endpoint === "function" ? endpoint() : endpoint;
    try {
      return (await this.axios.delete<T>(url, config)) as unknown as T;
    } catch (error) {
      this.logErrorDetails(error as CustomAxiosError);
      throw error;
    }
  }

  public async postWithFiles<T = unknown>(
    endpoint: string | EndpointFactory,
    formData: FormData,
    customTimeout?: number,
  ): Promise<T> {
    return this.requestWithFiles("post", endpoint, formData, customTimeout);
  }

  public async putWithFiles<T = unknown>(
    endpoint: string | EndpointFactory,
    formData: FormData,
  ): Promise<T> {
    return this.requestWithFiles("put", endpoint, formData);
  }

  public async patchWithFiles<T = unknown>(
    endpoint: string | EndpointFactory,
    formData: FormData,
  ): Promise<T> {
    return this.requestWithFiles("patch", endpoint, formData);
  }

  private async requestWithFiles<T = unknown>(
    method: "post" | "put" | "patch",
    endpoint: string | EndpointFactory,
    formData: FormData,
    customTimeout?: number,
  ): Promise<T> {
    const url = typeof endpoint === "function" ? endpoint() : endpoint;
    try {
      const config: AxiosRequestConfig = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      // Si un timeout personnalisé est fourni, l'utiliser
      if (customTimeout) {
        config.timeout = customTimeout;
      }

      return (await this.axios[method]<T>(
        url,
        formData,
        config,
      )) as unknown as T;
    } catch (error) {
      this.logErrorDetails(error as CustomAxiosError);
      throw error;
    }
  }

  private logErrorDetails(error: CustomAxiosError): void {
    if (error.response) {
      // Expected 401 on users/me for anonymous users — not an error worth logging
      const isAnonymousMeCheck =
        error.response.status === 401 &&
        (error.config?.method ?? "").toLowerCase() === "get" &&
        (error.config?.url ?? "").includes("users/me");
      if (isAnonymousMeCheck) return;

      console.error("[API Error]", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config?.url,
        method: error.config?.method,
      });
    } else if (error.request) {
      console.error("[API Network Error]", {
        message: "Aucune réponse reçue du serveur",
        url: error.config?.url,
        method: error.config?.method,
      });
    } else {
      console.error("[API Error]", error.message);
    }
  }

  /**
   * Resolves a backend `detail` string into a user-facing message.
   * Machine-readable codes (e.g. fastapi-users' LOGIN_BAD_CREDENTIALS) must
   * never reach the UI verbatim — only human-readable details (spaces/
   * lowercase) are safe to show directly; codes fall back to a translated
   * mapping, or to the status-based default when unmapped.
   */
  private resolveDetailMessage(detail: string, statusDefault: string): string {
    const isMachineCode = /^[A-Z][A-Z0-9_]*$/.test(detail);
    if (!isMachineCode) {
      return detail;
    }
    const translatedCode = tGlobal(`errors.auth_codes.${detail}`);
    const isTranslated = translatedCode && translatedCode !== `errors.auth_codes.${detail}`;
    return isTranslated ? translatedCode : statusDefault;
  }

  private formatErrorForUser(error: CustomAxiosError): UserFriendlyError {
    let userMessage =
      tGlobal("errors.server_error") ||
      "Une erreur est survenue. Veuillez réessayer.";
    let errorCode = "UNKNOWN_ERROR";

    if (error.response) {
      const status = error.response.status;

      switch (status) {
        case 400:
          userMessage =
            tGlobal("errors.invalid_data") ||
            "Données invalides. Veuillez vérifier les informations saisies.";
          errorCode = "INVALID_DATA";
          break;
        case 401:
          userMessage =
            tGlobal("errors.unauthorized") ||
            "Session expirée. Veuillez vous reconnecter.";
          errorCode = "UNAUTHORIZED";
          break;
        case 403:
          userMessage =
            tGlobal("errors.forbidden") ||
            "Vous n'avez pas les droits nécessaires pour cette action.";
          errorCode = "FORBIDDEN";
          break;
        case 404:
          userMessage =
            tGlobal("errors.not_found") ||
            "La ressource demandée n'existe pas ou a été supprimée.";
          errorCode = "NOT_FOUND";
          break;
        case 409:
          userMessage =
            tGlobal("errors.conflict") ||
            "Cette ressource existe déjà ou présente un conflit.";
          errorCode = "CONFLICT";
          break;
        case 500:
        case 502:
        case 503:
          userMessage =
            tGlobal("errors.server_error") ||
            "Le serveur a rencontré une erreur. Veuillez réessayer plus tard.";
          errorCode = "SERVER_ERROR";
          break;
      }

      const errorData = error.response.data as { message?: string; detail?: string | { msg?: string }[] } | null;
      if (errorData?.message) {
        userMessage = errorData.message;
      } else if (typeof errorData?.detail === "string") {
        userMessage = this.resolveDetailMessage(errorData.detail, userMessage);
      }
    } else if (error.request) {
      userMessage =
        tGlobal("errors.network") ||
        "Impossible de communiquer avec le serveur. Vérifiez votre connexion.";
      errorCode = "NETWORK_ERROR";
    }

    return {
      message: userMessage,
      code: errorCode,
      originalError: error,
    };
  }

  /**
   * Récupère le token CSRF depuis les cookies
   */
  private getCSRFTokenFromCookie(): string | null {
    try {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "X-CSRF-TOKEN") {
          return decodeURIComponent(value);
        }
      }
      return null;
    } catch (error) {
      console.warn(
        "Erreur lors de la récupération du token CSRF depuis les cookies:",
        error,
      );
      return null;
    }
  }
}

// Function to get public session token (outside the ApiService class to avoid interceptor loops)
export const getPublicSessionToken = async (): Promise<string | null> => {
  const buildEndpoint = (): string => {
    const configuredBase = (import.meta.env.VITE_APP_API_URL || "").replace(
      /\/$/,
      "",
    );
    const fallbackBase =
      typeof window !== "undefined"
        ? window.location.origin.replace(/\/$/, "")
        : "";
    const baseUrl = configuredBase || fallbackBase;
    if (!baseUrl) {
      return "/api/v1/auth/custom/public-session";
    }
    if (baseUrl.endsWith("/api/v1")) {
      return `${baseUrl}/auth/custom/public-session`;
    }
    return `${baseUrl}/api/v1/auth/custom/public-session`;
  };

  const requestToken = async (
    method: "get" | "post",
  ): Promise<string | null> => {
    const url = buildEndpoint();
    const response = await axios.request<{ access_token: string }>({
      url,
      method,
      data: method === "post" ? {} : undefined,
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": import.meta.env.VITE_API_KEY,
      },
      withCredentials: true,
    });

    return response.data.access_token;
  };

  try {
    return await requestToken("post");
  } catch (error) {
    const isAxiosError = axios.isAxiosError(error);
    const status = isAxiosError ? error.response?.status : undefined;
    if (status === 405) {
      try {
        return await requestToken("get");
      } catch (retryError) {
        console.error("Retry with GET failed for public session token:", retryError);
        return null;
      }
    }
    console.error("Failed to fetch public session token:", error);
    return null;
  }
};

export const apiService = new ApiService();
